import { useState } from "react"
import { Button, Card, Checkbox, Collapse, Empty, Flex, Form, Input, Radio, Select, Space, Spin, Tooltip } from "antd"
import { useWatch } from "antd/es/form/Form"
import { InfoCircleOutlined, PlusOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { isFeatureEnabled } from "config/features"
import { paypalLogoUrl } from "helpers/constant"
import { numberToCurrency } from "helpers/string"
import { colors, spacings } from "themes/variables"
import { Text } from "ui/Typography"
import NoCardImage from "ui/Icon/DSIcon/NoCardImage"
import TipSection from "ui/TipSection"
import PaymentMethodList from "pagesV2/pro/Bookings/BookingForm/PaymentMethodList"
import AddCardModal from "pagesV2/pro/Bookings/BookingForm/AddCardModal"
import stripeIcon from "assets/imagesV2/settings/stripe.svg"
import { getFormattedBookingDate } from "ui/Booking/Details/utils"
import { Avatar } from "ui/Avatar"
import { getMediaUrl } from "utils/media-upload"
import { getExperienceMainPhoto } from "pagesV2/pro/GuideProfile/GuideExperiences/utils/getExperienceMainPhoto"
import { roundToCents } from "utils/bookingCalculations"
import { LINK_PROVIDERS, OTHERS_TYPES, PAYMENT_TABS, paymentProvider } from "./constants"
import { useTakePaymentForm } from "./useTakePaymentForm"
import { usePaymentPreview } from "./usePaymentPreview"
import styles from "./index.module.css"

const TakePaymentForm = ({
  remainingBalance,
  bookingUUID,
  bookingId,
  onClose,
  bookingTitle,
  bookedOn,
  clientName,
  clientEmail,
  experienceAvatar,
  balanceTaxesAndFees,
  balanceFeeItems,
  orderUuid,
  orderExperiences
}) => {
  const [form] = Form.useForm()
  const [modalForm] = Form.useForm()
  const { t } = useTranslation()
  // An order's Collect Payment collects its whole balance at once; a partial amount is collected from
  // one of its experiences, picked from those that owe something.
  const isOrder = Boolean(orderUuid) && orderExperiences.length > 1
  const [pickedUuid, setPickedUuid] = useState(orderExperiences[0]?.uuid ?? null)
  const picked = isOrder ? orderExperiences.find((experience) => experience.uuid === pickedUuid) ?? orderExperiences[0] : null

  const {
    activeTab,
    setActiveTab,
    linkProvider,
    setLinkProvider,
    tipAmount,
    setTipAmount,
    isPartialPayment,
    setIsPartialPayment,
    isOfflineTab,
    selectedCardId,
    setSelectedCardId,
    isModalVisible,
    setIsModalVisible,
    isAddingCard,
    isSubmitting,
    isStripeConnected,
    isPaypalConnected,
    isStripeChargeable,
    stripeProblemMessage,
    paymentMethods,
    paymentMethodsLoading,
    clientId,
    visibleTabs,
    linkInfoText,
    amountLabel,
    partialLabel,
    totalCollectedLabel,
    submitLabel,
    handleSubmit,
    createPaymentMethod,
    feeSummaryData
  } = useTakePaymentForm({
    remainingBalance,
    bookingUUID: picked ? picked.uuid : bookingUUID,
    bookingId: picked ? picked.bookingId : bookingId,
    orderUuid: isOrder ? orderUuid : null,
    onClose,
    clientEmail,
    form,
    modalForm
  })

  // The two call sites supply different shapes: the booking-details page sends a single
  // experience_avatar, the reservation detail page sends trip.photos (an array). getMediaUrl only
  // understands a single media object, so an array silently rendered no photo at all.
  // For the array, reuse the app-wide "main photo" rule (position 0, else the first) rather than
  // assuming index 0. Videos are excluded — a video-only experience shows no image until the
  // backend can supply a thumbnail.
  const avatarUrl = Array.isArray(experienceAvatar)
    ? getExperienceMainPhoto({ photos: experienceAvatar.filter((media) => media?.file_type !== "video") })
    : getMediaUrl(experienceAvatar)

  const amountValue = useWatch("amount", form)

  // The balance comes from the backend's outstanding_breakdown: total = base (the price still owed)
  // + taxes_and_fees (taxes, plus any platform fee not yet paid). It is shown exactly as reported.
  // A platform fee already in the balance stays there on every tab, cash included: once added, it
  // is never removed (decision, 2026-09-30).
  const feeItemLabel = (item) => item?.label ?? item?.title
  const feeItemAmount = (item) => Number(item?.amount ?? item?.price) || 0
  const balanceFeeLines = (Array.isArray(balanceFeeItems) ? balanceFeeItems : [])
    .filter((item) => feeItemAmount(item) > 0)
    .map((item) => ({ label: feeItemLabel(item), amount: feeItemAmount(item) }))

  const balanceDue = roundToCents(Math.max(Number(remainingBalance) || 0, 0))
  // Unchecked, the guide collects the whole balance and there is no amount field to read.
  const collectedAmount = isPartialPayment ? Number(amountValue) || 0 : balanceDue

  // A partial amount can't exceed the balance it is taken from: the picked experience's on an order.
  const partialBalanceDue = picked ? roundToCents(Math.max(Number(picked.balance) || 0, 0)) : balanceDue

  // When there is no outstanding balance, the booking amount can't be paid — only a tip can be collected.
  const isZeroBalance = balanceDue <= 0

  // Null when the backend hasn't reported the split — the Summary then shows a single Remaining
  // Balance row rather than inventing a number.
  const taxesAndFeesDue = balanceTaxesAndFees != null ? roundToCents(Math.max(Number(balanceTaxesAndFees), 0)) : null
  const balanceBase = taxesAndFeesDue != null ? roundToCents(balanceDue - taxesAndFeesDue) : balanceDue

  // Every fee added on top of the amount — the platform fee still owed on a cash-on-day booking and
  // the payment processing fee — is the server's own quote (payments/preview), so what the screen
  // shows is exactly what gets charged. Cash and the other offline methods add no fees at all and
  // need no quote. A tip is card-only and the server never adds a fee to it.
  const isCardOrLink = activeTab === PAYMENT_TABS.CARD || activeTab === PAYMENT_TABS.LINK
  const previewProvider =
    activeTab === PAYMENT_TABS.LINK && linkProvider === LINK_PROVIDERS.PAYPAL ? paymentProvider.paypalLink : paymentProvider.card
  const previewTip = activeTab === PAYMENT_TABS.CARD ? tipAmount : 0

  // Summary: the whole outstanding balance. Partial block: the amount actually being collected.
  const balancePreview = usePaymentPreview({
    bookingId,
    orderUuid: isOrder ? orderUuid : null,
    amount: balanceDue,
    paymentProvider: previewProvider,
    tipAmount: previewTip,
    isEnabled: isCardOrLink && balanceDue > 0
  })
  const collectedPreview = usePaymentPreview({
    bookingId: picked ? picked.bookingId : bookingId,
    amount: collectedAmount,
    paymentProvider: previewProvider,
    tipAmount: previewTip,
    isEnabled: isCardOrLink && isPartialPayment && collectedAmount > 0 && collectedAmount <= partialBalanceDue
  })

  const feesFrom = (preview) => ({
    platformFee: Number(preview?.platform_fee) || 0,
    cardProcessingFee: Number(preview?.card_fee) || 0
  })
  const balanceFees = feesFrom(balancePreview.preview)
  const collectedFees = feesFrom(collectedPreview.preview)

  // Card and link charges wait for the quote: submitting before it arrives, or after it failed,
  // would charge an amount the guide never saw.
  const activePreview = isPartialPayment ? collectedPreview : balancePreview
  const isQuotePending = isCardOrLink && collectedAmount > 0 && (activePreview.isPending || activePreview.isError)
  const hasQuoteError = isCardOrLink && activePreview.isError

  const platformFeeTitle = feeSummaryData?.platform_fee?.title ?? "Platform Fee"
  const cardFeeLabel = t("client.payment.collect.card_fee")

  // Summary always describes the whole outstanding balance, regardless of any partial amount.
  const summaryTotal = roundToCents(balanceDue + tipAmount + balanceFees.platformFee)
  const summaryPaymentTotal = roundToCents(summaryTotal + balanceFees.cardProcessingFee)

  // Partial block: what is actually being charged now, and what stays owed afterwards.
  const totalCollected = roundToCents(collectedAmount + tipAmount + collectedFees.platformFee + collectedFees.cardProcessingFee)
  const outstandingAfter = roundToCents(Math.max(balanceDue - collectedAmount, 0))

  // One "label ......... value" line of the summary / partial blocks. An outstanding balance is
  // styled red via the CSS token; every other row keeps the modal's secondary text colour.
  const summaryRow = (label, value, { isBold = false, hasDivider = false, isOutstanding = false } = {}) => {
    const textProps = isOutstanding
      ? { className: styles.outstandingValue }
      : { color: isBold ? colors.textPrimary : colors.textSecondaryDark }
    return (
      <Flex justify="space-between" className={hasDivider ? styles.summaryDivider : ""}>
        <Text fw={isBold ? 700 : 400} {...textProps}>
          {label}
        </Text>
        <Text fw={isBold ? 700 : 400} {...textProps}>
          {value}
        </Text>
      </Flex>
    )
  }

  // Taxes & Fees expands to the per-fee lines the backend reports inside the balance, matching the
  // booking screens. Falls back to a plain row when the payload carries no itemisation.
  const taxesAndFeesLabel = t("client.payment.collect.taxes_and_fees")
  const taxesAndFeesRow =
    balanceFeeLines.length > 0 ? (
      <Collapse
        ghost
        className={styles.taxesFeesCollapse}
        items={[
          {
            key: "taxes-fees",
            styles: { header: { padding: 0 }, body: { padding: "4px 0 0 16px" } },
            label: summaryRow(taxesAndFeesLabel, numberToCurrency(taxesAndFeesDue)),
            children: (
              <Flex vertical gap={4}>
                {balanceFeeLines.map((line) => (
                  <Flex key={line.label} justify="space-between">
                    <Text color={colors.textSecondaryDark} size="sm">
                      {line.label}
                    </Text>
                    <Text color={colors.textSecondaryDark} size="sm">
                      {numberToCurrency(line.amount)}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            )
          }
        ]}
      />
    ) : (
      summaryRow(taxesAndFeesLabel, numberToCurrency(taxesAndFeesDue))
    )

  const amountField = (
    <Form.Item
      label={isOfflineTab ? amountLabel : null}
      name="amount"
      className={styles.partialAmountInput}
      rules={[
        { required: true, message: t("client.payment.collect.amount_required") },
        {
          validator: (_, value) => {
            if (!value) return Promise.reject()
            const num = Number.parseFloat(value)
            if (Number.isNaN(num) || num <= 0) return Promise.reject(t("client.payment.collect.amount_invalid"))
            if (num > partialBalanceDue) return Promise.reject(t("client.payment.amountToPayCannotExceedOutstandingBalance"))
            return Promise.resolve()
          }
        }
      ]}
    >
      <Input prefix="$" type="number" min="0.01" step="0.01" />
    </Form.Item>
  )

  return (
    <Spin spinning={isSubmitting} tip={t("client.payment.collect.processing")}>
      <Space direction="vertical" size={24} className="w-full">
        <div className={styles.formContent}>
          <Space direction="vertical" size={24} className="w-full">
            {(bookingTitle || clientName || clientEmail) && (
              <Flex gap={12} align="center" className={styles.bookingInfo}>
                {avatarUrl && <Avatar fullName={bookingTitle} src={avatarUrl} shape="square" size={64} />}
                <Flex vertical gap={4}>
                  {bookingTitle && (
                    <Text fw={500}>
                      {bookingTitle}
                      {bookedOn ? ` · ${getFormattedBookingDate(bookedOn)}` : ""}
                    </Text>
                  )}
                  {(clientName || clientEmail) && (
                    <Text color={colors.textSecondaryDark}>{[clientName, clientEmail].filter(Boolean).join(" · ")}</Text>
                  )}
                </Flex>
              </Flex>
            )}

            <div>
              <Text className={styles.sectionLabel}>{t("client.payment.collect.choose_payment_method")}</Text>
              <div className={styles.tabContainer}>
                {visibleTabs.map((tab) => (
                  <Tooltip key={tab.key} title={tab.disabled ? tab.tooltip : ""}>
                    <button
                      type="button"
                      className={`${activeTab === tab.key ? styles.tabButtonActive : styles.tabButton} ${tab.disabled ? styles.tabButtonDisabled : ""}`}
                      onClick={() => !tab.disabled && setActiveTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  </Tooltip>
                ))}
              </div>
            </div>

            <Flex gap={spacings.XS} className={styles.balancePill}>
              <Text color={colors.textSecondaryDark}>{t("client.payment.collect.remaining_balance_due")}</Text>
              <Text color={colors.textPrimary} fw={500}>
                {numberToCurrency(balanceDue)}
              </Text>
            </Flex>

            <Form form={form} layout="vertical" initialValues={{ amount: remainingBalance }}>
              <div className={styles.tabContent}>
                {activeTab === PAYMENT_TABS.CARD && (
                  <Spin spinning={paymentMethodsLoading}>
                    {paymentMethods?.length ? (
                      <>
                        <div className={styles.cardListContainer}>
                          <PaymentMethodList
                            active={selectedCardId}
                            setActive={setSelectedCardId}
                            paymentMethods={paymentMethods}
                            clientId={clientId}
                          />
                        </div>
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          style={{ color: colors.sportsmanBrand, fontWeight: 500, fontSize: 16, marginTop: 8 }}
                          onClick={() => setIsModalVisible(true)}
                        >
                          {t("booking.labels.add_card")}
                        </Button>
                      </>
                    ) : (
                      !isAddingCard && (
                        <Card>
                          <Empty
                            image={<NoCardImage />}
                            description={
                              <>
                                <p>{t("booking.labels.no_card_title")}</p>
                                <Button icon={<PlusOutlined />} type="primary" onClick={() => setIsModalVisible(true)}>
                                  {t("booking.labels.add_card")}
                                </Button>
                              </>
                            }
                          />
                        </Card>
                      )
                    )}
                  </Spin>
                )}

                {activeTab === PAYMENT_TABS.LINK && (
                  <Space direction="vertical" className="w-full">
                    <Flex gap={8}>
                      {isStripeConnected && (
                        <Tooltip title={!isStripeChargeable ? stripeProblemMessage : ""}>
                          <button
                            type="button"
                            className={`${linkProvider === LINK_PROVIDERS.STRIPE ? styles.providerButtonActive : styles.providerButton} ${
                              !isStripeChargeable ? styles.providerButtonDisabled : ""
                            }`}
                            onClick={() => isStripeChargeable && setLinkProvider(LINK_PROVIDERS.STRIPE)}
                          >
                            <img src={stripeIcon} alt="Stripe" height={16} /> Stripe
                          </button>
                        </Tooltip>
                      )}
                      {isPaypalConnected && isFeatureEnabled("PAYPAL_SUPPORT") && (
                        <button
                          type="button"
                          className={linkProvider === LINK_PROVIDERS.PAYPAL ? styles.providerButtonActive : styles.providerButton}
                          onClick={() => setLinkProvider(LINK_PROVIDERS.PAYPAL)}
                        >
                          <img src={paypalLogoUrl} alt="PayPal" height={16} /> PayPal
                        </button>
                      )}
                    </Flex>
                    <div className={styles.linkInfo}>
                      <Text color={colors.textSecondaryDark}>{linkInfoText}</Text>
                    </div>
                  </Space>
                )}

                {activeTab === PAYMENT_TABS.OTHERS && (
                  <Space direction="vertical" className="w-full">
                    <Form.Item name="othersType" label="Payment Type" rules={[{ required: true, message: "Please select a payment type" }]}>
                      <Select placeholder="Select payment type" options={OTHERS_TYPES} />
                    </Form.Item>
                    <Form.Item name="note" label="Reference / Note">
                      <Input.TextArea placeholder="Add a reference number or note (optional)" rows={3} />
                    </Form.Item>
                  </Space>
                )}
              </div>

              {activeTab === PAYMENT_TABS.CARD && (
                <TipSection baseAmount={remainingBalance} onTipChange={setTipAmount} disablePresets={isZeroBalance} />
              )}

              <Spin spinning={isCardOrLink && balancePreview.isLoading}>
                <Flex vertical gap={4} className={styles.summarySection}>
                  <Text className={styles.sectionLabel}>{t("client.payment.collect.summary")}</Text>
                  {summaryRow(t("client.payment.collect.remaining_balance"), numberToCurrency(balanceBase))}
                  {tipAmount > 0 && summaryRow(t("client.payment.collect.tip"), numberToCurrency(tipAmount))}
                  {taxesAndFeesDue > 0 && taxesAndFeesRow}
                  {balanceFees.platformFee > 0 && summaryRow(platformFeeTitle, `+${numberToCurrency(balanceFees.platformFee)}`)}
                  {summaryRow(t("client.payment.collect.total"), numberToCurrency(summaryTotal), { isBold: true })}
                  {balanceFees.cardProcessingFee > 0 && (
                    <>
                      {summaryRow(cardFeeLabel, `+${numberToCurrency(balanceFees.cardProcessingFee)}`, { hasDivider: true })}
                      {summaryRow(t("client.payment.collect.payment_total"), numberToCurrency(summaryPaymentTotal), { isBold: true })}
                    </>
                  )}
                  {hasQuoteError && (
                    <Flex align="center" gap={spacings.XS} wrap>
                      <Text className={styles.outstandingValue}>{t("client.payment.collect.preview_failed")}</Text>
                      <Button type="link" size="small" onClick={activePreview.retry}>
                        {t("client.payment.collect.preview_retry")}
                      </Button>
                    </Flex>
                  )}
                </Flex>
              </Spin>

              {!isZeroBalance && (
                <Flex vertical gap={spacings.S} className={styles.partialSection}>
                  <Checkbox checked={isPartialPayment} onChange={(event) => setIsPartialPayment(event.target.checked)}>
                    {partialLabel}
                  </Checkbox>

                  {isOrder && isPartialPayment && (
                    <Flex vertical gap={spacings.XS}>
                      <Text color={colors.textSecondaryDark}>{t("booking.reservation_detail.picker_collect_title")}</Text>
                      <Radio.Group
                        value={picked.uuid}
                        onChange={(event) => {
                          setPickedUuid(event.target.value)
                          // The amount was for the previous experience's balance.
                          form.setFieldValue("amount", "")
                        }}
                      >
                        <Flex vertical gap={spacings.S}>
                          {orderExperiences.map((experience) => (
                            <Radio key={experience.uuid} value={experience.uuid}>
                              <Flex vertical gap={2}>
                                <Text fw={500}>{experience.title}</Text>
                                <Text color={colors.textSecondaryDark} size="sm">
                                  #{experience.number} ·{" "}
                                  {t("booking.reservation_detail.picker_balance", { amount: numberToCurrency(experience.balance) })}
                                </Text>
                              </Flex>
                            </Radio>
                          ))}
                        </Flex>
                      </Radio.Group>
                    </Flex>
                  )}

                  {isPartialPayment && !isOfflineTab && (
                    <Spin spinning={collectedPreview.isLoading}>
                      <Flex vertical gap={4}>
                        <Flex justify="space-between" align="center" gap={spacings.S}>
                          <Text color={colors.textSecondaryDark}>{amountLabel}</Text>
                          {amountField}
                        </Flex>
                        {collectedFees.platformFee > 0 && summaryRow(platformFeeTitle, `+${numberToCurrency(collectedFees.platformFee)}`)}
                        {collectedFees.cardProcessingFee > 0 &&
                          summaryRow(cardFeeLabel, `+${numberToCurrency(collectedFees.cardProcessingFee)}`)}
                        {summaryRow(totalCollectedLabel, numberToCurrency(totalCollected), { isBold: true, hasDivider: true })}
                        {summaryRow(t("client.payment.collect.outstanding_balance"), numberToCurrency(outstandingAfter), {
                          isOutstanding: true
                        })}
                      </Flex>
                    </Spin>
                  )}

                  {isPartialPayment && isOfflineTab && (
                    <div className={styles.cashPartialGrid}>
                      <Flex vertical gap={spacings.XS}>
                        {amountField}
                        {activeTab === PAYMENT_TABS.CASH && (
                          <div className={styles.infoCallout}>
                            <InfoCircleOutlined className={styles.infoCalloutIcon} />
                            <Text color={colors.textSecondaryDark}>{t("client.payment.collect.cash_info")}</Text>
                          </div>
                        )}
                      </Flex>
                      <Flex vertical gap={4}>
                        {summaryRow(totalCollectedLabel, numberToCurrency(totalCollected), { isBold: true })}
                        {summaryRow(t("client.payment.collect.outstanding_balance"), numberToCurrency(outstandingAfter), {
                          isOutstanding: true,
                          hasDivider: true
                        })}
                      </Flex>
                    </div>
                  )}
                </Flex>
              )}
            </Form>
          </Space>
        </div>

        <Flex gap={spacings.S}>
          <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
            {t("global.cancel")}
          </Button>
          <Button
            type="primary"
            className="py-l px-xl"
            disabled={(isZeroBalance && tipAmount <= 0) || isQuotePending}
            onClick={() => handleSubmit(collectedAmount)}
          >
            {submitLabel}
          </Button>
        </Flex>
      </Space>

      {isModalVisible && (
        <AddCardModal
          visible={isModalVisible}
          onCancel={() => {
            modalForm.resetFields()
            setIsModalVisible(false)
          }}
          onSubmit={createPaymentMethod}
          form={modalForm}
          loading={isAddingCard}
        />
      )}
    </Spin>
  )
}

TakePaymentForm.propTypes = {
  remainingBalance: PropTypes.number,
  bookingUUID: PropTypes.string,
  bookingId: PropTypes.number,
  onClose: PropTypes.func,
  bookingTitle: PropTypes.string,
  bookedOn: PropTypes.string,
  clientName: PropTypes.string,
  clientEmail: PropTypes.string,
  experienceAvatar: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  // outstanding_breakdown.taxes_and_fees — taxes plus any platform fee still owed
  balanceTaxesAndFees: PropTypes.number,
  // outstanding_breakdown.items — the per-fee lines inside the balance
  balanceFeeItems: PropTypes.array,
  // An order's Collect Payment: the order, and its experiences that owe something
  // ({ uuid, bookingId, title, number, balance }), offered for a partial amount.
  orderUuid: PropTypes.string,
  orderExperiences: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      bookingId: PropTypes.number,
      title: PropTypes.string,
      number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      balance: PropTypes.number
    })
  )
}

TakePaymentForm.defaultProps = {
  orderUuid: null,
  orderExperiences: []
}

export default TakePaymentForm
